import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppLayout } from "@/components/AppLayout";
import { RoleGuard } from "@/components/RoleGuard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { CardDestaque, COR_STATUS, PageHeader, SecaoCard } from "@/components/padroes";
import { ExportarMenu } from "@/components/beneficios/ExportarMenu";
import { api } from "@/lib/api";
import { formatCents, formatDateTime, formatMesRef } from "@/lib/format";
import { cn } from "@/lib/utils";
import { formatDataCivil, type FolhaBeneficioResumo, type PaginaFolhaBeneficio } from "@/data/beneficios";
import { toast } from "sonner";
import { ChevronRight, Loader2, Plus } from "lucide-react";

const hojeCivil = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

/** "outubro de 2026" -> "Outubro de 2026". */
const nomeDoMes = (aaaamm: string) => {
  const s = formatMesRef(aaaamm);
  return s.charAt(0).toUpperCase() + s.slice(1);
};

interface MesDeFolhas {
  /** AAAA-MM */
  mes: string;
  folhas: FolhaBeneficioResumo[];
  total: number;
  /** Maior número de pessoas numa folha do mês (as folhas extras repetem gente). */
  maxPessoas: number;
  naoExportadas: number;
}

/** Agrupa pela competência; a API já devolve da mais nova para a mais antiga. */
const agruparPorMes = (folhas: FolhaBeneficioResumo[]): MesDeFolhas[] => {
  const grupos = new Map<string, FolhaBeneficioResumo[]>();
  for (const f of folhas) {
    const mes = f.competencia;
    grupos.set(mes, [...(grupos.get(mes) ?? []), f]);
  }
  return [...grupos.entries()]
    .sort(([a], [b]) => b.localeCompare(a))
    .map(([mes, fs]) => ({
      mes,
      folhas: fs,
      total: fs.reduce((s, f) => s + f.total, 0),
      maxPessoas: Math.max(0, ...fs.map((f) => f.totalColaboradores)),
      naoExportadas: fs.filter((f) => !f.exportadaEm).length,
    }));
};

/**
 * Próximo crédito: a folha não exportada com a data mais próxima a partir de hoje;
 * se todas as pendentes já passaram, a mais recente delas.
 */
const escolherProxima = (folhas: FolhaBeneficioResumo[]) => {
  const pendentes = folhas.filter((f) => !f.exportadaEm);
  const hoje = hojeCivil();
  const futuras = pendentes.filter((f) => f.data >= hoje).sort((a, b) => a.data.localeCompare(b.data));
  return futuras[0] ?? pendentes[0] ?? null;
};

const StatusMes = ({ m }: { m: MesDeFolhas }) => {
  const texto =
    m.naoExportadas === 0
      ? "Exportado"
      : m.naoExportadas === m.folhas.length
        ? "Não exportado"
        : `${m.naoExportadas} não exportada${m.naoExportadas > 1 ? "s" : ""}`;
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold",
        m.naoExportadas > 0
          ? "bg-amber-500/15 text-amber-700 dark:text-amber-400"
          : "bg-muted text-muted-foreground",
      )}
    >
      {texto}
    </span>
  );
};

const RHBeneficios = () => {
  const nav = useNavigate();
  // Lista inteira (sem `page`): o agrupamento por mês é feito aqui.
  const [folhas, setFolhas] = useState<FolhaBeneficioResumo[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [mesesAbertos, setMesesAbertos] = useState<Set<string> | null>(null);
  const [pendenciasProxima, setPendenciasProxima] = useState<number | null>(null);

  const [novaOpen, setNovaOpen] = useState(false);
  const [competencia, setCompetencia] = useState(hojeCivil().slice(0, 7));
  const [data, setData] = useState(hojeCivil());
  const [descricao, setDescricao] = useState("");
  const [aplicarFixos, setAplicarFixos] = useState(true);
  const [criando, setCriando] = useState(false);

  const load = useCallback(async () => {
    try {
      setFolhas(await api.get<FolhaBeneficioResumo[]>("/beneficios/folhas"));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao carregar as folhas de benefícios");
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const meses = useMemo(() => agruparPorMes(folhas), [folhas]);
  const proxima = useMemo(() => escolherProxima(folhas), [folhas]);

  // Os 2 meses mais recentes começam abertos (só na primeira carga; depois vale o que o RH abriu).
  useEffect(() => {
    if (mesesAbertos === null && meses.length > 0) setMesesAbertos(new Set(meses.slice(0, 2).map((m) => m.mes)));
  }, [meses, mesesAbertos]);

  // Pendências de cadastro vêm do resumo do detalhe; uma linha basta.
  const proximaId = proxima?.id;
  useEffect(() => {
    setPendenciasProxima(null);
    if (!proximaId) return;
    api
      .get<PaginaFolhaBeneficio>(`/beneficios/folhas/${proximaId}?page=1&pageSize=1`)
      .then((r) => setPendenciasProxima(r.resumo.pendenciasCadastro.total))
      .catch(() => setPendenciasProxima(null));
  }, [proximaId]);

  const alternarMes = (mes: string) =>
    setMesesAbertos((atual) => {
      const novo = new Set(atual ?? []);
      if (novo.has(mes)) novo.delete(mes);
      else novo.add(mes);
      return novo;
    });

  const abrirNova = () => {
    setCompetencia(hojeCivil().slice(0, 7));
    setData(hojeCivil());
    setDescricao("");
    setAplicarFixos(true);
    setNovaOpen(true);
  };

  const criar = async () => {
    setCriando(true);
    try {
      const folha = await api.post<FolhaBeneficioResumo>("/beneficios/folhas", {
        competencia,
        data,
        aplicarFixos,
        ...(descricao.trim() ? { descricao: descricao.trim() } : {}),
      });
      toast.success(`Folha de benefícios de ${formatMesRef(competencia)} criada`);
      setNovaOpen(false);
      nav(`/rh/beneficios/${folha.id}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao criar folha de benefícios");
    } finally {
      setCriando(false);
    }
  };

  const mesDaProxima = proxima ? meses.find((m) => m.mes === proxima.competencia) : undefined;

  return (
    <RoleGuard role="rh">
      <AppLayout title="Benefícios">
        <div className="mx-auto flex max-w-[1280px] flex-col gap-4 tabular-nums">
          <PageHeader
            titulo="Benefícios"
            subtitulo={
              <span className="block max-w-[680px]">
                Cada folha reúne os benefícios do cartão Flash de uma competência, com a data do pagamento, e gera a
                planilha de pedido. Benefícios não entram no cálculo da folha de pagamento.
              </span>
            }
            acoes={
              <Button onClick={abrirNova}>
                <Plus />
                Nova folha de benefícios
              </Button>
            }
          />

          {proxima && (
            <CardDestaque
              to={`/rh/beneficios/${proxima.id}`}
              corStatus={COR_STATUS.ambar}
              rotulo="Próximo crédito · não exportada"
              titulo={
                <>
                  {nomeDoMes(proxima.competencia)}
                  <span className="mt-1 block text-[13px] font-normal tracking-normal text-muted-foreground">
                    Pagamento em {formatDataCivil(proxima.data)}
                    {proxima.descricao && ` · ${proxima.descricao}`}
                  </span>
                </>
              }
              acao="Abrir folha"
              metricas={[
                { rotulo: "Colaboradores", valor: proxima.totalColaboradores },
                { rotulo: "Total do mês", valor: formatCents(mesDaProxima?.total ?? proxima.total) },
                {
                  rotulo: "Pendências de cadastro",
                  valor:
                    pendenciasProxima === null
                      ? "—"
                      : pendenciasProxima === 0
                        ? "Nenhuma"
                        : `${pendenciasProxima} colaborador${pendenciasProxima === 1 ? "" : "es"}`,
                  tom: pendenciasProxima ? "alerta" : undefined,
                },
              ]}
            />
          )}

          <SecaoCard
            titulo="Folhas por mês"
            acoes={
              <span className="text-xs text-muted-foreground">
                Folhas extras do mês (admissões, auxílios) aparecem dentro do mês.
              </span>
            }
            semPadding
          >
            <Table className="min-w-[860px]">
              <TableHeader>
                <TableRow>
                  <TableHead className="pl-5 sm:pl-6">Competência / Folha</TableHead>
                  <TableHead className="w-[120px]">Pagamento</TableHead>
                  <TableHead className="w-[130px] text-right">Colaboradores</TableHead>
                  <TableHead className="w-[160px] text-right">Total</TableHead>
                  <TableHead className="w-[170px]">Exportada em</TableHead>
                  <TableHead className="w-[84px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {carregando && folhas.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="py-12 text-center text-muted-foreground">
                      <Loader2 className="mx-auto h-5 w-5 animate-spin" />
                    </TableCell>
                  </TableRow>
                ) : meses.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="py-12 text-center text-muted-foreground">
                      Nenhuma folha de benefícios criada ainda.
                    </TableCell>
                  </TableRow>
                ) : (
                  meses.map((m) => {
                    const aberto = mesesAbertos?.has(m.mes) ?? false;
                    return (
                      <Fragment key={m.mes}>
                        <TableRow
                          className="cursor-pointer hover:bg-muted"
                          onClick={() => alternarMes(m.mes)}
                          aria-expanded={aberto}
                        >
                          <TableCell className="pl-5 font-semibold sm:pl-6">
                            <span className="flex items-center gap-2">
                              <ChevronRight
                                className={cn(
                                  "h-4 w-4 shrink-0 text-muted-foreground transition-transform",
                                  aberto && "rotate-90",
                                )}
                              />
                              {nomeDoMes(m.mes)}
                              <span className="text-[11px] font-medium text-muted-foreground">
                                {m.folhas.length} {m.folhas.length === 1 ? "folha" : "folhas"}
                              </span>
                            </span>
                          </TableCell>
                          <TableCell />
                          <TableCell className="text-right text-muted-foreground">{m.maxPessoas} pessoas</TableCell>
                          <TableCell className="text-right font-semibold">{formatCents(m.total)}</TableCell>
                          <TableCell>
                            <StatusMes m={m} />
                          </TableCell>
                          <TableCell />
                        </TableRow>
                        {aberto &&
                          m.folhas.map((f) => (
                            <TableRow
                              key={f.id}
                              className="cursor-pointer bg-muted-soft/40 hover:bg-primary/5"
                              onClick={() => nav(`/rh/beneficios/${f.id}`)}
                            >
                              <TableCell className="py-2.5 pl-11 font-medium sm:pl-12">
                                {f.descricao ?? <span className="text-muted-foreground">Sem descrição</span>}
                              </TableCell>
                              <TableCell className="py-2.5 text-muted-foreground">{formatDataCivil(f.data)}</TableCell>
                              <TableCell className="py-2.5 text-right">{f.totalColaboradores}</TableCell>
                              <TableCell className="py-2.5 text-right font-medium">{formatCents(f.total)}</TableCell>
                              <TableCell
                                className={cn(
                                  "py-2.5",
                                  f.exportadaEm ? "text-muted-foreground" : "text-amber-700 dark:text-amber-400",
                                )}
                              >
                                {f.exportadaEm ? formatDateTime(f.exportadaEm) : "Não exportada"}
                              </TableCell>
                              <TableCell className="py-1.5 pr-3" onClick={(e) => e.stopPropagation()}>
                                <div className="flex items-center justify-end gap-1">
                                  <ExportarMenu
                                    folhaId={f.id}
                                    compacto
                                    variant="ghost"
                                    disabled={f.total === 0}
                                    onExportado={() => void load()}
                                  />
                                  <ChevronRight
                                    className="h-4 w-4 cursor-pointer text-muted-foreground"
                                    onClick={() => nav(`/rh/beneficios/${f.id}`)}
                                  />
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                      </Fragment>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </SecaoCard>
        </div>
      </AppLayout>

      <Dialog open={novaOpen} onOpenChange={setNovaOpen}>
        <DialogContent className="max-w-[420px]">
          <DialogHeader>
            <DialogTitle>Nova folha de benefícios</DialogTitle>
            <DialogDescription>
              A competência é o mês a que o benefício se refere; a data de pagamento é quando ele cai ou é comprado
              (o de novembro pode ser pago em outubro). Depois você lança e ajusta os valores de cada colaborador.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="nova-competencia">Competência</Label>
                <Input
                  id="nova-competencia"
                  type="month"
                  value={competencia}
                  onChange={(e) => setCompetencia(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="nova-data">Data de pagamento</Label>
                <Input id="nova-data" type="date" value={data} onChange={(e) => setData(e.target.value)} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="nova-descricao">Descrição (opcional)</Label>
              <Input
                id="nova-descricao"
                value={descricao}
                onChange={(e) => setDescricao(e.target.value)}
                maxLength={200}
                placeholder="Ex: Benefícios de outubro"
              />
            </div>
            <div className="flex items-start gap-2.5">
              <Checkbox
                id="nova-fixos"
                checked={aplicarFixos}
                onCheckedChange={(v) => setAplicarFixos(v === true)}
                className="mt-0.5"
              />
              <Label htmlFor="nova-fixos" className="text-sm font-normal leading-snug">
                Já incluir os benefícios padrão
                <span className="mt-0.5 block text-xs leading-relaxed text-muted-foreground">
                  Cada colaborador ativo recebe os fixos do vínculo dele (Configurações), ou os valores próprios
                  definidos no cadastro
                </span>
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNovaOpen(false)} disabled={criando}>
              Cancelar
            </Button>
            <Button onClick={() => void criar()} disabled={!competencia || !data || criando}>
              {criando && <Loader2 className="animate-spin" />}
              Criar folha
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </RoleGuard>
  );
};

export default RHBeneficios;
